import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardDataContext } from '../contexts/DashboardDataContext';
import { Case } from '../types';
import {
  buildCaseNoteSummaries,
  filterCaseNoteSummaries,
  listSessionRailFilters,
  SessionRailFilter,
  CaseNoteSummary,
} from '../components/MeetingNotes/meetingNotesUtils';
import { loadNotesByCaseIds } from '../components/Cases/meetingNotesUtils';
import { t } from '../utils/translations';

export function useMeetingNotesData() {
  const { currentUser } = useAuth();
  const { cases: cachedCases, loading: dashboardLoading, sessionReportCounts } =
    useDashboardDataContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [allSummaries, setAllSummaries] = useState<CaseNoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'others'>('active');
  const [selectedSessionFilter, setSelectedSessionFilter] =
    useState<SessionRailFilter | null>(null);

  const selectedCaseId = searchParams.get('caseId');
  const prevCaseIdRef = useRef<string | null>(null);

  const loadData = useCallback(async () => {
    if (!currentUser?.id || dashboardLoading) return;

    try {
      setLoading(true);
      setError(null);

      const visibleCases = cachedCases;
      const visibleCaseIds = new Set(visibleCases.map((c) => c.id));

      const notesByCaseId = await loadNotesByCaseIds(Array.from(visibleCaseIds));

      setAllSummaries(buildCaseNoteSummaries(visibleCases, notesByCaseId));
    } catch (err) {
      console.error('Error loading meeting notes page:', err);
      setError(t.meetingNotes.loadError);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, cachedCases, dashboardLoading]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredSummaries = useMemo(
    () => filterCaseNoteSummaries(allSummaries, searchTerm, statusFilter),
    [allSummaries, searchTerm, statusFilter]
  );

  // Open the most recently noted case by default. The summaries are already
  // ordered by lastNoteDate descending; deep links keep their explicit case.
  useEffect(() => {
    if (loading || selectedCaseId || filteredSummaries.length === 0) return;
    const next = new URLSearchParams(searchParams);
    next.set('caseId', filteredSummaries[0].case.id);
    setSearchParams(next, { replace: true });
  }, [
    loading,
    selectedCaseId,
    filteredSummaries,
    searchParams,
    setSearchParams,
  ]);

  const selectedSummary = useMemo(() => {
    if (!selectedCaseId) return null;
    return (
      filteredSummaries.find((s) => s.case.id === selectedCaseId) ??
      allSummaries.find((s) => s.case.id === selectedCaseId) ??
      null
    );
  }, [selectedCaseId, filteredSummaries, allSummaries]);

  const sessionRailFilters = useMemo(
    () => (selectedSummary ? listSessionRailFilters(selectedSummary.notes) : []),
    [selectedSummary]
  );

  useEffect(() => {
    const caseChanged = selectedCaseId !== prevCaseIdRef.current;
    prevCaseIdRef.current = selectedCaseId;

    if (!selectedSummary) {
      setSelectedSessionFilter(null);
      return;
    }

    const filters = listSessionRailFilters(selectedSummary.notes);
    if (filters.length === 0) {
      setSelectedSessionFilter(null);
      return;
    }

    setSelectedSessionFilter((current) => {
      if (!caseChanged && current != null && filters.includes(current)) {
        return current;
      }
      return filters[0];
    });
  }, [selectedCaseId, selectedSummary]);

  const selectCase = useCallback(
    (caseId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('caseId', caseId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const reportCountForSelected = selectedSummary
    ? sessionReportCounts[selectedSummary.case.id] ?? 0
    : 0;

  return {
    loading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    filteredSummaries,
    selectedCaseId,
    selectedSummary,
    selectCase,
    selectedSessionFilter,
    setSelectedSessionFilter,
    sessionRailFilters,
    reportCountForSelected,
    refetch: loadData,
    getReportCount: (caseItem: Case) => sessionReportCounts[caseItem.id] ?? 0,
  };
}
